import { ITreeItem, SpecialDIIds } from "@kuika/kuika-cl-model";
import React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";

// Shared by the breadcrumb segments and the sub-bar screen name, which lists the screen root's
// children on hover (KOA-18608) — one list, one order, one set of test ids.

// The test-id spelling of a node label: no whitespace, lower case. Mirrors the transform the
// designer already uses for generated ids (`toTestIdSlug` in shared/utility/cascader-search.tsx,
// which is private to that module). `toLowerCase` rather than `toLocaleLowerCase`: the latter
// folds "I" to "ı" under tr-TR and would give a Turkish-locale browser different ids.
const toBreadcrumbTestIdSlug = (value: string): string => value.replace(/\s+/g, "").toLowerCase();

const HIDDEN_CHILD_NAMES: readonly string[] = ["Drawer", "Modal", "FloatPanel"];

// Stable handle for one breadcrumb node's test-ids. A friendlyName is unique per screen
// (KAgentHelper.createNewFriendlyName), so it addresses a node unambiguously; the screen-root
// node carries no friendlyName and renders the screen name, so it gets a literal "root".
export const getTreeItemTestIdKey = (x: ITreeItem): string => {
  if (x.kuikaId?.toString() === SpecialDIIds.rootDI.toString()) return "root";
  const source: string = x.friendlyName ?? x.name ?? "";
  return toBreadcrumbTestIdSlug(source);
};

export const getTreeItemDisplayName = (sd: IScreenDesignContextState, x: ITreeItem): string => {
  if (x.kuikaId && x.kuikaId.toString() === SpecialDIIds.rootDI.toString()) {
    return sd.sdName;
  }
  if (x.friendlyName) {
    return `${x.friendlyName} (${x.name})`;
  }
  return x.name;
};

// The screen root lists its children in Component Tree order: Header -> Body -> Footer -> Layout Items.
const orderRootChildren = (children: ITreeItem[]): ITreeItem[] => {
  const findById = (id: string): ITreeItem | undefined => children.find((s) => s.kuikaId.toString() === id);
  const leadingIds: string[] = [SpecialDIIds.headerDI.toString(), SpecialDIIds.pageHeaderDI.toString()];
  const trailingIds: string[] = [
    SpecialDIIds.pageFooterDI.toString(),
    SpecialDIIds.footerDI.toString(),
    SpecialDIIds.layoutHeaderDI.toString(),
    SpecialDIIds.layoutLeftDI.toString(),
    SpecialDIIds.layoutRightDI.toString(),
    SpecialDIIds.layoutFooterDI.toString()
  ];
  const specialIds: string[] = [...leadingIds, ...trailingIds];
  const others: ITreeItem[] = children.filter((s) => !specialIds.includes(s.kuikaId.toString()));
  const leading: ITreeItem[] = leadingIds.map(findById).filter((s): s is ITreeItem => s != null);
  const trailing: ITreeItem[] = trailingIds.map(findById).filter((s): s is ITreeItem => s != null);
  return [...leading, ...others, ...trailing];
};

export const getTreeItemChildMenuItems = (sd: IScreenDesignContextState, x: ITreeItem): KuikaDropdownMenuItemType[] => {
  const visible: ITreeItem[] = (x.children || []).filter((s) => !HIDDEN_CHILD_NAMES.includes(s.name));
  const children: ITreeItem[] =
    x.kuikaId.toString() === SpecialDIIds.rootDI.toString() ? orderRootChildren(visible) : visible;

  return children.map((s: ITreeItem): KuikaDropdownMenuItemType => ({
    key: s.kuikaId.toString(),
    label: <KuikaSmallMediumText>{getTreeItemDisplayName(sd, s)}</KuikaSmallMediumText>,
    // Explicit id required, not a nicety: KuikaDropdownMenu falls back to
    // `"dropdown" + label.toString() + "Button"`, and `label` here is a React node — so
    // every child in every breadcrumb dropdown rendered the identical
    // `dropdown[object Object]Button`. Measured on test-master: hovering Row1 produced three
    // items all carrying that same id, which is a strict-mode collision and unaddressable.
    dataTestId: `breadcrumbChild-${getTreeItemTestIdKey(s)}`,
    onClick: (): void => {
      sd.highlightHoveredItem(s.kuikaId.toString());
      sd.setSelectedDisplayItem(s.kuikaId.toString());
    }
  }));
};
